import en from './messages/en.json'
import pl from './messages/pl.json'

export const SUPPORTED_LOCALES = ['en', 'pl'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

/**
 * Message catalogs. `en.json` is the source of truth: every key path present
 * there must exist in every other locale (enforced by i18n.test.ts).
 */
export const messages = { en, pl }

/**
 * Endonyms — each language is labeled in its own script (UI strings must not
 * translate a language's name into another language).
 */
export const LOCALE_ENDONYMS: Record<AppLocale, string> = {
  en: 'English',
  pl: 'Polski',
}

/**
 * CLDR plural rules as choice-index functions for `t(key, n)` on
 * pipe-separated choice messages. vue-i18n v9 does not bundle per-locale
 * plural rules, so they are provided here.
 *
 * Choice order per locale:
 *   en: [one, other]   (2 choices)
 *   pl: [one, few, many] (3 choices)
 */
const enPluralRule = (n: number): number =>
  Math.abs(n) === 1 ? 0 : 1

const plPluralRule = (n: number): number => {
  const abs = Math.abs(n)
  const int = Math.floor(abs)
  if (abs - int !== 0) return 2 // decimals → many
  if (int === 1) return 0 // one: 1
  const mod10 = int % 10
  const mod100 = int % 100
  // few: 2-4, but not 12-14
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 1
  return 2 // many: 0, 5-21 (except 22-24), 25-29, 31-34, ... and 12-14
}

export const PLURAL_RULES: Record<AppLocale, (n: number) => number> = {
  en: enPluralRule,
  pl: plPluralRule,
}
